import { api } from '@/config/api';

export type AiGenerationKind = 'image' | 'copy' | 'video' | 'audio';
export type AiGenerationStatus = 'queued' | 'processing' | 'succeeded' | 'failed';
export type AiGenerationSource = 'generated' | 'uploaded' | 'combined';

export interface AiGeneration {
  _id: string;
  generationUid: string;
  companyUid: string;
  createdByUserUid: string;
  kind: AiGenerationKind;
  status: AiGenerationStatus;
  source?: AiGenerationSource;
  prompt: string;
  enhancedPrompt?: string;
  platformPreset?: string;
  provider: string;
  model: string;
  assetUrl?: string;
  imagekitFileId?: string;
  thumbnailUrl?: string;
  mimeType?: string;
  originalFilename?: string;
  title?: string;
  copyText?: string;
  error?: string;
  costUsd?: number;
  durationMs?: number;
  /** Video/audio clip length in seconds (Veo 3 clips are a fixed ~8s). */
  durationSec?: number;
  /** Image-to-video: the frame the clip animates from. */
  sourceImageUrl?: string;
  createdAt: string;
}

export interface AiStudioPreset {
  key: string;
  label: string;
  kind: 'image' | 'copy' | 'video';
  aspectRatio?: string;
  hint: string;
}

// Mirror of backend/src/modules/slighter-crm/ai-studio/platform-presets.ts
// (same pattern as meta-matrix.ts — backend is the source of truth).
export const AI_STUDIO_PRESETS: AiStudioPreset[] = [
  { key: 'ig_post',         label: 'Instagram Post',    kind: 'image', aspectRatio: '1:1',  hint: 'Square Instagram feed post.' },
  { key: 'ig_story',        label: 'Instagram Story',   kind: 'image', aspectRatio: '9:16', hint: 'Vertical Instagram story.' },
  { key: 'tiktok',          label: 'TikTok',            kind: 'image', aspectRatio: '9:16', hint: 'Vertical TikTok visual.' },
  { key: 'fb_feed',         label: 'Facebook Feed',     kind: 'image', aspectRatio: '4:5',  hint: 'Facebook feed ad image.' },
  { key: 'youtube_thumb',   label: 'YouTube Thumbnail', kind: 'image', aspectRatio: '16:9', hint: 'YouTube thumbnail.' },
  { key: 'whatsapp_status', label: 'WhatsApp Status',   kind: 'image', aspectRatio: '9:16', hint: 'Vertical WhatsApp status image.' },
  { key: 'video_vertical',  label: 'Reels / TikTok / Shorts', kind: 'video', aspectRatio: '9:16', hint: 'Vertical short-form video (~8s).' },
  { key: 'video_landscape', label: 'Landscape / Feed',        kind: 'video', aspectRatio: '16:9', hint: 'Landscape video (~8s).' },
  { key: 'ig_caption',         label: 'Instagram Caption',  kind: 'copy', hint: 'Engaging caption with hashtags.' },
  { key: 'tiktok_hook',        label: 'TikTok Hook',        kind: 'copy', hint: 'Punchy scroll-stopping hook.' },
  { key: 'fb_ad_primary',      label: 'Facebook Ad Text',   kind: 'copy', hint: 'Benefit-led ad primary text.' },
  { key: 'whatsapp_broadcast', label: 'WhatsApp Broadcast', kind: 'copy', hint: 'Short personal broadcast message.' },
  { key: 'linkedin_post',      label: 'LinkedIn Post',      kind: 'copy', hint: 'Professional thought-leadership post.' },
];

const BASE = '/slighter_crm/ai-studio';

export const aiStudioService = {
  create: async (input: {
    kind: AiGenerationKind;
    prompt: string;
    platformPreset?: string;
    enhance?: boolean;
    model?: string;
    voiceId?: string;
    /** Video only — image-to-video: the clip animates from this frame. */
    sourceImageUrl?: string;
  }): Promise<AiGeneration> => (await api.post(`${BASE}/generations`, input)).data,

  /** Image + video models available to this company (live ∩ allowlist), for the dropdowns. */
  listModels: async (): Promise<{
    image: Array<{ id: string; label: string; default: boolean }>;
    video: Array<{ id: string; label: string; default: boolean }>;
  }> => (await api.get(`${BASE}/models`)).data,

  list: async (params: {
    kind?: AiGenerationKind;
    page?: number;
    limit?: number;
    /** Picker mode — only succeeded, hosted image/video assets. */
    mediaOnly?: boolean;
    mediaType?: 'image' | 'video';
    source?: 'generated' | 'uploaded' | 'combined';
    q?: string;
  }): Promise<{
    items: AiGeneration[]; total: number; page: number; limit: number;
  }> => (await api.get(`${BASE}/generations`, { params })).data,

  get: async (generationUid: string): Promise<AiGeneration> =>
    (await api.get(`${BASE}/generations/${generationUid}`)).data,

  /** Hard delete — removes the ImageKit file AND the DB record. */
  remove: async (generationUid: string): Promise<{ ok: boolean; imagekitDeleted: boolean }> =>
    (await api.delete(`${BASE}/generations/${generationUid}`)).data,

  /** Upload a file straight into the Media Library (not AI generated). */
  uploadMedia: async (file: File, title?: string): Promise<AiGeneration> => {
    const fd = new FormData();
    fd.append('file', file);
    if (title) fd.append('title', title);
    return (await api.post(`${BASE}/generations/upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data;
  },

  /** Save a browser-merged combined video (video + voiceover). */
  saveCombined: async (
    file: Blob,
    opts: { sourceVideoUid?: string; sourceAudioUid?: string; durationSec?: number; title?: string },
  ): Promise<AiGeneration> => {
    const fd = new FormData();
    fd.append('file', new File([file], 'combined.mp4', { type: 'video/mp4' }));
    if (opts.sourceVideoUid) fd.append('sourceVideoUid', opts.sourceVideoUid);
    if (opts.sourceAudioUid) fd.append('sourceAudioUid', opts.sourceAudioUid);
    if (opts.durationSec) fd.append('durationSec', String(opts.durationSec));
    if (opts.title) fd.append('title', opts.title);
    return (await api.post(`${BASE}/generations/combine`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data;
  },

  getConfig: async (): Promise<{ elevenLabsConfigured: boolean }> =>
    (await api.get(`${BASE}/config`)).data,
};
